import { pushToDatalayer } from '@luks/meta/src';
import {
  buildForm,
  buildHtml,
  buildLink,
  KitchenSinkRoute,
  RouteSlotInput,
} from '@luks/ui';
import { useLocation } from '@reach/router';
import { memoize } from 'lodash';

import {
  LiveSpecialistFragment,
  SpecialistSearchQuery,
  useInfiniteSpecialistSearchQuery,
} from '../../../generated/queries';
import { formNavigate, useQueryParameters } from '../../commons/forms';
import { isDefined } from '../../commons/is-defined';
import {
  specialistFiltersUrlQueryConstruct,
  specialistFiltersUrlQueryParse,
} from '../../commons/specialist-filters-url-query';
import { buildDrupalImage } from '../Image';

const Form = buildForm<{
  text: string;
  department: string;
  glossary: string;
  location: string;
  spokenLanguage: string;
  jobFunction: string;
}>({
  initialValues: {
    text: '',
    department: '',
    glossary: '',
    location: '',
    spokenLanguage: '',
    jobFunction: '',
  },
  onSubmit: (values) => {
    formNavigate()(specialistFiltersUrlQueryConstruct(values));
  },
  useInitialValues() {
    const search = useLocation().search;
    return specialistFiltersUrlQueryParse(search);
  },
});

type SpecialistGalleryItem = Exclude<
  RouteSlotInput<typeof KitchenSinkRoute, 'body', 'SpecialistGallery'>['input'],
  Function
>['specialists'][number];

const mapSpecialist = memoize(
  (item: LiveSpecialistFragment): SpecialistGalleryItem => {
    return {
      title: [item.title].filter((word) => !!word && word !== '').join(' '),
      Image: item.imagePortrait
        ? buildDrupalImage(item.imagePortrait, 'teaser', undefined, {
            objectPosition: 'top',
          })
        : undefined,
      Link: buildLink({ href: item.path }),
      gender: item.gender as 'male' | 'female',
      Description: buildHtml(
        [item.job, ...(item.areas?.map((area) => area?.title) || [])]
          .filter((word) => !!word && word !== '')
          .join(', '),
      ),
    };
  },
);

function useSpecialistListingQueryParameters() {
  const values = useQueryParameters();
  const params = new URLSearchParams(values);
  let defaultOverride = '';
  if (typeof window !== 'undefined') {
    defaultOverride = window.location.search;
  }
  return specialistFiltersUrlQueryParse(params.toString(), {
    ...specialistFiltersUrlQueryParse(defaultOverride),
  });
}

const aggregateResults = (pages?: Array<SpecialistSearchQuery>) =>
  pages?.reduce(
    (acc, value) => [
      ...acc,
      ...value.listingSpecialists.items.filter(isDefined).map(mapSpecialist),
    ],
    [] as Array<SpecialistGalleryItem>,
  ) || [];

export function mapSpecialistGallery(
  _: any,
  context: { items: Array<any>; index: number },
): RouteSlotInput<typeof KitchenSinkRoute, 'body', 'SpecialistGallery'> {
  return {
    key: 'SpecialistGallery',
    input: function useSpecialistGallery() {
      const pageSize = 12;
      const params = useSpecialistListingQueryParameters();

      const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
      } = useInfiniteSpecialistSearchQuery(
        {
          filters: params,
          limit: pageSize,
          offset: 0,
        },
        {
          keepPreviousData: true,
          getNextPageParam: (lastPage, allPages) => {
            const displayedItems = allPages.reduce(
              (acc, val) => acc + (val.listingSpecialists.items.length || 0),
              0,
            );

            return lastPage.listingSpecialists.total > displayedItems
              ? {
                  limit: pageSize,
                  filters: params,
                  offset: displayedItems,
                }
              : undefined;
          },
        },
      );

      if (
        data &&
        data.pages.length === 1 && // Do not send data on "load more".
        !isLoading &&
        !isFetching &&
        !isFetchingNextPage
      ) {
        pushToDatalayer(
          {
            event: 'SpecialistSearch',
            SearchBox: params.text,
            'Standort Filter': params.location,
            'Abteilung Filter': params.department,
            'Glossar Filter': params.glossary,
            'Sprache Filter': params.spokenLanguage,
          },
          'specialist-search',
        );
      }

      const latest = data?.pages.at(-1);
      return [
        {
          glossary: latest?.listingSpecialists.facets.glossary || [],
          specialists: aggregateResults(data?.pages),
          departments:
            latest?.listingSpecialists.facets.department.map((item) => ({
              value: item,
              title: item,
            })) || [],
          locations:
            latest?.listingSpecialists.facets.location.map((item) => ({
              value: item,
              title: item,
            })) || [],
          spokenLanguages:
            latest?.listingSpecialists.facets.spokenLanguage.map((item) => ({
              value: item,
              title: item,
            })) || [],
          jobFunctions:
            latest?.listingSpecialists.facets.jobFunction.map((item) => ({
              id: item.label as string,
              label: item.label as string,
              count: item.count,
              active: false,
              Link: buildLink({
                onClick: (ev) => {
                  ev.preventDefault();
                },
              }),
            })) || [],
          Form,
          infiniteScroll: context.items.length - 1 === context.index,
          MoreLink: hasNextPage
            ? buildLink({
                onClick: (ev) => {
                  ev.preventDefault();
                  fetchNextPage();
                },
              })
            : undefined,
        },
        isLoading || isFetching || isFetchingNextPage
          ? 102
          : data
          ? (latest?.listingSpecialists?.total || 0) > 0
            ? 200
            : 404
          : 0,
      ];
    },
  };
}
